import { FunctionComponent, useState } from "react";
import clsx from "clsx";
import RiskToleranceComponent from "./RiskToleranceComponent";
import { Level, CardConfig, RiskToleranceCardConfig } from "./@types";
import styles from "./paranoid-mode.module.scss";

interface IRiskToleranceCardProps {
  currentLevel: number;
  isConfigurable: boolean;
  isSelectable: boolean;
  isSelected?: boolean;
  setSelectedLevel?: (newSelectedLevel: Level) => void;
  enabledComponents?: CardConfig;
  descriptionProps: RiskToleranceCardConfig;
}

const RiskToleranceCard: FunctionComponent<IRiskToleranceCardProps> = ({
  currentLevel,
  isConfigurable,
  isSelectable,
  isSelected,
  setSelectedLevel,
  enabledComponents,
  descriptionProps: {
    level,
    name,
    id,
    rl_auxiliary_analysis: auxiliary,
    network_references: network,
    rlcs,
    joe,
    yara
  }
}) => {
  const [isConfiguring, setIsConfiguring] = useState<boolean>(false);
  return (
    <div
      className={clsx(
        styles.card,
        setSelectedLevel && styles["card-clickable"],
        isSelected && styles["card-selected"]
      )}
      data-testid={`paranoid-select-level-${level}`}
      onClick={() => {
        if (isSelectable) {
          setSelectedLevel?.(level);
        }
      }}
    >
      <div className={styles[`card-item-first-${id}`]}>
        {isSelected && (
          <span
            className={clsx(styles["selected-card-check"], "icon-checked")}
          />
        )}
        <span className={styles["selected-card-text"]}>
          <b>{name}</b>
          {level === currentLevel && <i> (Active)</i>}
        </span>
        {isConfigurable && (
          <a
            className={styles.configure}
            onClick={() => setIsConfiguring(prev => !prev)}
          >
            {isConfiguring ? "Close Configuration" : "Configure"}
          </a>
        )}
      </div>
      <RiskToleranceComponent
        name="Auxiliary Analysis"
        iconName="auxiliary-analysis"
        fieldName="rl_auxiliary_analysis"
        level={level}
        score={auxiliary.score}
        enabled={enabledComponents?.[level].includes("rl_auxiliary_analysis")}
        showCheckbox={isConfiguring}
      />
      <RiskToleranceComponent
        name="Network Threat Intelligence"
        iconName="cloud-intelligence"
        fieldName="network_references"
        level={level}
        enabled={enabledComponents?.[level].includes("network_references")}
        showCheckbox={isConfiguring}
        displayMessage={
          network.third_party_verdicts !== undefined ? (
            <span>
              Apply the classification of the payload to the URL
              {!!network.third_party_verdicts &&
                " and consult verdicts of 3rd party reputation sources"}
              {network.third_party_verdicts === 2 &&
                ` (at least ${network.third_party_verdicts})`}
              .
            </span>
          ) : undefined
        }
      />
      <RiskToleranceComponent
        name="RL Cloud Sandbox"
        iconName="cloud-sandbox"
        fieldName="rlcs"
        score={rlcs.score}
        level={level}
        enabled={enabledComponents?.[level].includes("rlcs")}
        showCheckbox={isConfiguring}
        displayMessage={
          <span>
            Can change final classification from <b>Suspicious</b> to{" "}
            <b>Malicious</b>.
          </span>
        }
        fontIcon={false}
      />
      <RiskToleranceComponent
        name="Joe Sandbox"
        iconName="joe"
        fieldName="joe"
        level={level}
        enabled={enabledComponents?.[level].includes("joe")}
        showCheckbox={isConfiguring}
        displayMessage={
          joe.confidence !== undefined ? (
            <span>
              <b>Malicious</b> and <b>Suspicious</b> analyses with confidence of
              at least <b>{joe.confidence}</b> (out of 5) can change the final
              classification.
            </span>
          ) : undefined
        }
        fontIcon={false}
      />
      <RiskToleranceComponent
        name="YARA"
        iconName="yara"
        fieldName="yara"
        level={level}
        enabled={enabledComponents?.[level].includes("yara")}
        showCheckbox={isConfiguring}
        displayMessage={
          yara.yara_forge_ruleset && (
            <span>
              Enables the{" "}
              <b>YARA Forge {yara.yara_forge_ruleset.capitalize()}</b> Ruleset.
            </span>
          )
        }
      />
    </div>
  );
};

export default RiskToleranceCard;
